import { useLocation } from "@gatsbyjs/reach-router"
import React from "react"
import { Helmet } from "react-helmet"
import { useTranslation } from "react-i18next"

import ogImageAsset from "../../assets/images/clickpass/main-logo.png"
import { ENV } from "../../constants/env"

// Absolute origin + path. Crawlers read the server-rendered HTML, where `window`
// does not exist, so the URL must never be derived from it — doing so collapsed
// every canonical onto the site root and told Google each page was a duplicate
// of the homepage.
const absoluteUrl = path => {
  if (!path) return ENV.CANONICAL_ORIGIN
  if (/^https?:\/\//i.test(path)) return path
  return `${ENV.CANONICAL_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`
}

// Gatsby is configured with the default `trailingSlash: "always"`, so the
// canonical has to match the URL actually served or the two disagree.
const withTrailingSlash = pathname => {
  if (!pathname) return "/"
  if (pathname.includes(".")) return pathname // /sitemap.xml and friends
  return pathname.endsWith("/") ? pathname : `${pathname}/`
}

// `<` is escaped so a description containing "</script>" cannot close the tag.
const serializeJsonLd = data => JSON.stringify(data).replace(/</g, "\\u003c")

const Head = ({ title, meta = [], description, image, url, keywords, pathname, noindex = false, jsonLd, children }) => {
  const { t, i18n } = useTranslation("meta")
  const location = useLocation()

  const pageTitle = title || t("defaultTitle")
  const pageDescription = description || t("defaultDescription")
  const pageKeywords = keywords || t("defaultKeywords")
  const pageImage = absoluteUrl(image || ogImageAsset)

  const pageUrl = url || absoluteUrl(withTrailingSlash(pathname || location?.pathname))

  const structuredData = [].concat(jsonLd || []).filter(Boolean)

  const metaTags = [
    {
      name: "description",
      content: pageDescription
    },
    // `follow` so links on a page we keep out of the index still get crawled.
    ...(noindex ? [{ name: "robots", content: "noindex, follow" }] : []),
    {
      name: "keywords",
      content: pageKeywords
    },
    // Open Graph tags
    {
      property: "og:site_name",
      content: t("siteName")
    },
    {
      property: "og:title",
      content: pageTitle
    },
    {
      property: "og:description",
      content: pageDescription
    },
    {
      property: "og:type",
      content: "website"
    },
    {
      property: "og:url",
      content: pageUrl
    },
    {
      property: "og:image",
      content: pageImage
    },
    {
      property: "og:image:alt",
      content: pageTitle
    },
    {
      property: "og:locale",
      content: (i18n.resolvedLanguage || "es-MX").replace("-", "_")
    },
    // Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image"
    },
    {
      name: "twitter:title",
      content: pageTitle
    },
    {
      name: "twitter:description",
      content: pageDescription
    },
    {
      name: "twitter:image",
      content: pageImage
    },
    {
      name: "twitter:image:alt",
      content: pageTitle
    },
    ...meta
  ]

  return (
    <>
      <Helmet
        defaultTitle={t("defaultTitle")}
        title={title}
        meta={metaTags}
        htmlAttributes={{ lang: i18n.resolvedLanguage || "es-MX" }}
      >
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="canonical" href={pageUrl} />
        {structuredData.map((data, index) => (
          <script key={`ld-${index}`} type="application/ld+json">
            {serializeJsonLd(data)}
          </script>
        ))}
        {children}
      </Helmet>
    </>
  )
}

export default Head
